import { DispositionScheme } from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import { mapProvisionToRecipient } from '@/modules/dispositiveProvisions/dispositiveProvisions.utils';
import {
  DispositiveProvisionsForm_DispositionScenario,
  DispositiveProvisionsFormRecipient,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { GetInitialValuesParams } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.utils';
import {
  DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE,
  DispositiveProvisionsFormV2_DeathEventProvisions,
  DispositiveProvisionsFormV2_DispositionScenario,
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { sortDispositiveProvisions } from '@/modules/dispositiveProvisions/DispositiveProvisionsListView/DispositiveProvisionsListView.utils';
import {
  DispositiveProvisions_DispositionScenarioFragment,
  DispositiveProvisions_DispositiveProvisionFragment,
} from '@/modules/dispositiveProvisions/graphql/DispositiveProvisions.fragments.generated';
import { TransferMethod } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { calculateResiduaryRecipients } from './components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';

interface GetInitialValuesV2Params extends GetInitialValuesParams {
  isTwoClientHousehold: boolean;
  isInEstate: boolean;
}

/**
 * Returns the initial values for the Dispositive Provisions Form V2 from the
 * DispositionScenario data from the API.
 *
 * @param primaryClientIds
 * @param firstDeathPrimaryClientId
 * @param isTwoClientHousehold
 * @param dispositionScenarios
 * @param isInEstate
 */
export function getInitialValuesV2({
  primaryClientIds,
  firstDeathPrimaryClientId,
  isTwoClientHousehold,
  dispositionScenarios,
  isInEstate,
}: GetInitialValuesV2Params): DispositiveProvisionsFormV2Shape {
  const secondDeathPrimaryClientId = primaryClientIds.find(
    (id) => id !== firstDeathPrimaryClientId
  );

  const dispositionScenario = dispositionScenarios?.find(
    (scenario) => scenario.firstGrantorDeath?.id === firstDeathPrimaryClientId
  );

  const transferMethod =
    dispositionScenario?.transferMethod || TransferMethod.InKind;

  return {
    [DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE]: {
      selectedFirstPrimaryClientDeathId: firstDeathPrimaryClientId,
      dispositionScenarioId: dispositionScenario?.id || null,
      _dispositionScenarioAssociatedWaterfallId:
        dispositionScenario?.associatedHypotheticalWaterfall?.id || null,
      transferMethod,
      firstDeath: mapDispositionScenarioDataToV2Provisions({
        dispositionScenario,
        deathEvent: PrimaryClientDeathEvent.FIRST_DEATH,
        isTwoClientHousehold,
        isInEstate,
      }),
      secondDeath:
        isTwoClientHousehold && secondDeathPrimaryClientId
          ? mapDispositionScenarioDataToV2Provisions({
              dispositionScenario,
              deathEvent: PrimaryClientDeathEvent.SECOND_DEATH,
              isTwoClientHousehold,
              isInEstate,
            })
          : null,
    },
  };
}

/**
 * Maps the DispositionScenario data from the API to DispositiveProvisionsFormV2
 * provisions for a given death event.
 *
 * @param dispositionScenario
 * @param deathEvent
 * @param isTwoClientHousehold
 * @param isInEstate
 */
function mapDispositionScenarioDataToV2Provisions({
  dispositionScenario,
  deathEvent,
  isTwoClientHousehold,
  isInEstate,
}: {
  dispositionScenario:
    | DispositiveProvisions_DispositionScenarioFragment
    | undefined;
  deathEvent: PrimaryClientDeathEvent;
  isTwoClientHousehold: boolean;
  isInEstate: boolean;
}): DispositiveProvisionsFormV2_DeathEventProvisions {
  const defaultResiduaryPaysAllTaxes = isInEstate;

  return {
    deathEvent,
    recipients: mapDPScenarioDataToDPFormRecipients({
      dispositionScenario,
      deathEvent,
      isTwoClientHousehold,
    }),
    templateId:
      deathEvent === PrimaryClientDeathEvent.FIRST_DEATH
        ? (dispositionScenario?.dispositiveProvisionsTemplate?.id ?? null)
        : (dispositionScenario?.secondDeathDispositiveProvisionsTemplate?.id ??
          null),
    residuaryPaysAllTaxes:
      deathEvent === PrimaryClientDeathEvent.FIRST_DEATH
        ? (dispositionScenario?.firstDeathResiduaryPaysAllTaxes ??
          defaultResiduaryPaysAllTaxes)
        : (dispositionScenario?.secondDeathResiduaryPaysAllTaxes ??
          defaultResiduaryPaysAllTaxes),
  };
}

/**
 * Maps the DispositiveProvisions of a DispositionScenario from the API to
 * DispositiveProvisionsFormRecipients.
 *
 * @param dispositionScenario
 * @param deathEvent
 * @param isTwoClientHousehold
 */
function mapDPScenarioDataToDPFormRecipients({
  dispositionScenario,
  deathEvent,
  isTwoClientHousehold,
}: {
  dispositionScenario:
    | DispositiveProvisions_DispositionScenarioFragment
    | undefined;
  deathEvent: PrimaryClientDeathEvent;
  isTwoClientHousehold: boolean;
}): DispositiveProvisionsFormRecipient[] {
  let provisions: DispositiveProvisions_DispositiveProvisionFragment[] = [];

  if (deathEvent === PrimaryClientDeathEvent.FIRST_DEATH) {
    if (dispositionScenario?.dispositiveProvisionsTemplate) {
      provisions = getNodes(
        dispositionScenario.dispositiveProvisionsTemplate.dispositiveProvisions
      );
    } else {
      provisions = getNodes(dispositionScenario?.dispositiveProvisions);
    }
  } else {
    if (dispositionScenario?.secondDeathDispositiveProvisionsTemplate) {
      provisions = getNodes(
        dispositionScenario.secondDeathDispositiveProvisionsTemplate
          .dispositiveProvisions
      );
    } else {
      provisions = getNodes(
        dispositionScenario?.secondDeathDispositiveProvisions
      );
    }
  }

  if (
    !isTwoClientHousehold &&
    deathEvent === PrimaryClientDeathEvent.SECOND_DEATH
  ) {
    return [];
  }

  return provisions
    .sort(sortDispositiveProvisions)
    .map(mapProvisionToRecipient);
}

/**
 * Converts a DispositiveProvisionsFormV2_DispositionScenario to a
 * DispositiveProvisionsForm_DispositionScenario for submission.
 *
 * @param v2FormScenario
 * @param v2FormProvision
 */
export function convertDPFormV2ScenarioToDPFormV1Scenario(
  v2FormScenario: DispositiveProvisionsFormV2_DispositionScenario,
  v2FormProvision: DispositiveProvisionsFormV2_DeathEventProvisions
): DispositiveProvisionsForm_DispositionScenario {
  // Re-calculate the residuary recipients and verify that the "bearsTax"
  // field is set to "true" for all residuary recipients, if the
  // "residuaryPaysAllTaxes" field is set to "true".
  if (v2FormProvision.residuaryPaysAllTaxes) {
    const { residuaryIndices } = calculateResiduaryRecipients(
      v2FormProvision.recipients
    );

    v2FormProvision.recipients.forEach((recipient, index) => {
      recipient.bearsTax = residuaryIndices.includes(index);
    });
  }

  return {
    selectedFirstPrimaryClientDeathId:
      v2FormScenario.selectedFirstPrimaryClientDeathId,
    dispositionScenarioId: v2FormScenario.dispositionScenarioId,
    _dispositionScenarioAssociatedWaterfallId:
      v2FormScenario._dispositionScenarioAssociatedWaterfallId,
    dispositionScheme:
      v2FormProvision.deathEvent === PrimaryClientDeathEvent.FIRST_DEATH
        ? DispositionScheme.UPON_FIRST_DEATH
        : DispositionScheme.UPON_SECOND_DEATH,
    recipients: v2FormProvision.recipients,
    _residuaryPaysAllTaxes: v2FormProvision.residuaryPaysAllTaxes,
    templateId: v2FormProvision.templateId,
    _hasBeenReviewed: true,
    transferMethod: v2FormScenario.transferMethod,
  };
}

/**
 * Returns the base path for the death event in the Dispositive Provisions Form V2.
 *
 * NOTE: for backwards compatibility with the V1 DispositiveProvisionsRecipient,
 * this allows deathEvent to be null or undefined, in which case it defaults to
 * the first death base path.
 *
 * @param deathEvent
 */
export function getDeathEventBasePath(
  deathEvent: PrimaryClientDeathEvent | null | undefined
): `${typeof DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.${'firstDeath' | 'secondDeath'}` {
  return `${DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.${getDeathEventPathValue(
    deathEvent
  )}` as `${typeof DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.${'firstDeath' | 'secondDeath'}`;
}

export function getDeathEventPathValue(
  deathEvent: PrimaryClientDeathEvent | null | undefined
): 'firstDeath' | 'secondDeath' {
  return deathEvent === PrimaryClientDeathEvent.SECOND_DEATH
    ? 'secondDeath'
    : 'firstDeath';
}

interface ShouldShowFirstDeathSectionParams {
  /** The PULID of the disposing object (entity, client profile, or testamentary entity) */
  id: string;
  isEditingRootTarget: boolean;
  hasIncomingPouroverDisposition: boolean;
  isClientProfile: boolean;
  isTwoClientHousehold: boolean;
  isFirstToDie: boolean;
  firstDeathRecipientsCount: number;
  secondDeathRecipientsCount: number;
}

/**
 * Determines whether the first-death section should be shown in the DP form.
 *
 * For entities (root targets) and pourover targets, both sections are always shown.
 * For client profiles in two-client households, normally only the relevant death
 * section is shown (first-death for first-to-die clients, second-death for
 * second-to-die clients). However, AI-extracted dispositions may be placed on the
 * "wrong" death event — in that case, both sections must be visible so the user
 * can view and delete those dispositions.
 */
export function shouldShowFirstDeathSection({
  id,
  isEditingRootTarget,
  hasIncomingPouroverDisposition,
  isClientProfile,
  isTwoClientHousehold,
  isFirstToDie,
  firstDeathRecipientsCount,
  secondDeathRecipientsCount,
}: ShouldShowFirstDeathSectionParams): boolean {
  if (isEditingRootTarget && getPulidKind(id) === PulidKind.Entity) {
    return true;
  }

  if (hasIncomingPouroverDisposition) {
    return true;
  }

  // For client profiles in two-client households, the form normally shows
  // only one death section (based on which client dies first). However,
  // AI-extracted dispositions may be placed on the "other" death event.
  // Show both sections when recipients exist on the normally-hidden death
  // event, so that the user can view and delete those dispositions.
  if (isClientProfile && isTwoClientHousehold) {
    // First-to-die clients default to seeing the first death section;
    // show both if there are also second death recipients.
    if (isFirstToDie && secondDeathRecipientsCount > 0) {
      return true;
    }

    // Second-to-die clients default to seeing the second death section;
    // show both if there are also first death recipients.
    if (!isFirstToDie && firstDeathRecipientsCount > 0) {
      return true;
    }
  }

  return false;
}
